/**
 * Active Buying Stage Cache Service
 *
 * Fetches the Active Buying template from the backend API and caches it per
 * licence state. State-aware (unlike activeListingStageCache which is
 * state-agnostic):
 *   - TX  → 14 stages (post-contract-execution overlay)
 *   - DEFAULT (any state outside SUPPORTED_STATES) → 13 stages
 *   - CA / CO → empty list (milestone-driven, no overlay)
 *
 * Mirrors rialty-mobile-app's activeBuyingStageCache.service.ts and the
 * backend's ActiveBuyingStageDefinitions.java.
 */

import { apiClient, API_ENDPOINTS } from '../config/api';

// Per-state cache: { "TX": [...], "CA": [], ... }
const cache = {};
const inFlight = {};

const normalizeState = (state) => {
  const trimmed = (state ?? '').trim().toUpperCase();
  return trimmed || 'CA';
};

/**
 * Fetch the Active Buying stage template for the given state.
 * Concurrent calls for the same state are deduped. force=true bypasses cache.
 */
export const loadActiveBuyingStages = (state, force = false) => {
  const key = normalizeState(state);
  if (cache[key] !== undefined && !force) {
    return Promise.resolve();
  }
  if (inFlight[key]) return inFlight[key];

  inFlight[key] = (async () => {
    try {
      const token = localStorage.getItem('firebaseToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await apiClient.get(API_ENDPOINTS.activeBuyingStages, {
        headers,
        params: { state: key },
      });
      const data = response.data;

      if (Array.isArray(data?.stages)) {
        cache[key] = data.stages;
        console.log(
          `[ActiveBuyingStageCache] Loaded ${cache[key].length} stages for state=${key}`,
        );
      } else {
        console.warn(
          `[ActiveBuyingStageCache] Unexpected API response shape for state=${key}`,
        );
      }
    } catch (error) {
      console.warn(
        `[ActiveBuyingStageCache] Failed to load stages for state=${key}:`,
        error?.message || error,
      );
    } finally {
      delete inFlight[key];
    }
  })();

  return inFlight[key];
};

/** Whether the stage template for the given state has been successfully loaded. */
export const areActiveBuyingStagesLoaded = (state) =>
  cache[normalizeState(state)] !== undefined;

/** Ensure the stage template for the given state is loaded. */
export const ensureActiveBuyingStagesLoaded = (state) =>
  loadActiveBuyingStages(state);

/** Get the cached stage template for a given state. Returns [] until first successful load. */
export const getCachedActiveBuyingStages = (state) =>
  cache[normalizeState(state)] ?? [];

/** Get display metadata for a given stage id in the given state. */
export const getActiveBuyingStageMeta = (state, stageId) =>
  cache[normalizeState(state)]?.find((s) => s.id === stageId);
